import React from 'react';
import { Link } from 'react-router-dom';
import './FAQ.css';

const FAQ = () => {
  return (
    <div className="faq-page">
      <div className="faq-container">
        <Link to="/play" className="back-link">
          ← Back to Game
        </Link>

        <h1 className="faq-title">FAQ</h1>
        <p className="faq-subtitle">Frequently Asked Questions & How to Play</p>

        <div className="faq-content">
          {/* Getting Started */}
          <section className="faq-category">
            <h2 className="category-title">Getting Started</h2>

            <div className="faq-item">
              <h3 className="faq-question">What is Carnivores Adapting Terribly?</h3>
              <div className="faq-answer">
                <p>
                  Carnivores Adapting Terribly is an online multiplayer party game similar to Cards
                  Against Humanity. Players compete to create the funniest, most inappropriate, or
                  most clever card combinations. It's designed for adults 18+ and features mature
                  content.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">How do I create a game?</h3>
              <div className="faq-answer">
                <ol>
                  <li>Click "Create Game" on the home page</li>
                  <li>Enter a username</li>
                  <li>You'll receive a room code</li>
                  <li>Share the room code with friends</li>
                  <li>Start the game when everyone has joined</li>
                </ol>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">How do I join a game?</h3>
              <div className="faq-answer">
                <ol>
                  <li>Click "Join Game" on the home page</li>
                  <li>Enter your username</li>
                  <li>Enter the room code shared by the game creator</li>
                  <li>Wait in the lobby for the game to start</li>
                </ol>
              </div>
            </div>
          </section>

          {/* Game Rules */}
          <section className="faq-category">
            <h2 className="category-title">Game Rules & How to Play</h2>

            <div className="faq-item">
              <h3 className="faq-question">How does the game work?</h3>
              <div className="faq-answer">
                <p>Each round follows this pattern:</p>
                <ol>
                  <li>
                    <strong>Question Card:</strong> One player becomes the Judge and a question card
                    with a prompt is revealed
                  </li>
                  <li>
                    <strong>Answer Cards:</strong> All other players choose answer card(s) from
                    their hand to complete the prompt
                  </li>
                  <li>
                    <strong>Judging:</strong> The Judge reads all submissions and picks the funniest
                    one
                  </li>
                  <li>
                    <strong>Scoring:</strong> The winning player gets a point
                  </li>
                  <li>
                    <strong>Next Round:</strong> The Judge role rotates to the next player
                  </li>
                </ol>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">What is the Judge?</h3>
              <div className="faq-answer">
                <p>
                  The Judge is the judge for the round. They read the question card prompt and all
                  player submissions, then choose the funniest or best combination. The Card Czar
                  doesn't submit cards themselves. The role rotates to a different player each
                  round.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">How many cards do I get?</h3>
              <div className="faq-answer">
                <p>
                  You start with 10 answer cards in your hand. After each round, you draw back up to
                  10 cards. Some question cards require you to play 2 or even 3 answer cards to
                  complete the prompt.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">How do I win?</h3>
              <div className="faq-answer">
                <p>
                  The game creator sets how many rounds to play. The player with the most points at
                  the end wins! You earn points by having the Judge choose your card combination.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">What are Reno House Rules?</h3>
              <div className="faq-answer">
                <p>
                  Reno House Rules are optional special rules that add variety to gameplay. Features
                  may include wagering, special abilities, and other twists. More details coming
                  soon as features are implemented!
                </p>
              </div>
            </div>
          </section>

          {/* Room Management */}
          <section className="faq-category">
            <h2 className="category-title">Room Management</h2>

            <div className="faq-item">
              <h3 className="faq-question">What is a Co-Host?</h3>
              <div className="faq-answer">
                <p>
                  A Co-Host (also called Deputy) is a backup room manager designated by the Game
                  Owner. They have the same powers as the Game Owner:
                </p>
                <ul>
                  <li>Kick players from the room</li>
                  <li>Add or remove bot players</li>
                  <li>Manage room settings during gameplay</li>
                </ul>
                <p>
                  <strong>Why use a Co-Host?</strong> If the Game Owner disconnects or leaves, the
                  Co-Host automatically becomes the new Game Owner. This ensures someone can always
                  manage the room.
                </p>
                <p className="note">
                  <strong>Note:</strong> The Co-Host feature is optional. Game Owners can enable it
                  in the lobby using the "🎖️ Co-Host" toggle button.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">What is a Voyeur?</h3>
              <div className="faq-answer">
                <p>
                  Voyeurs are viewers who can watch the game without playing. They see all the
                  action but cannot submit cards, vote, or affect gameplay. Perfect for friends who
                  want to watch and laugh along!
                </p>
                <p>
                  <strong>Joining as Voyeur:</strong> When joining a game, you'll see two buttons:
                  "Join as Player" and "Join as Voyeur" (if enabled by the Game Owner).
                </p>
              </div>
            </div>
          </section>

          {/* Bot Players */}
          <section className="faq-category">
            <h2 className="category-title">Bot Players</h2>

            <div className="faq-item">
              <h3 className="faq-question">What are bot players?</h3>
              <div className="faq-answer">
                <p>
                  Bot players are computer-controlled players that automatically submit cards.
                  They're perfect for smaller groups or when you want to test the game. Bots are
                  clearly marked with "Bot" in their names.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">How many bots can I add?</h3>
              <div className="faq-answer">
                <p>
                  You can add up to 2 bots per game. The room creator can add or remove bots from
                  the game lobby before starting.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">Can bots be the Judge?</h3>
              <div className="faq-answer">
                <p>
                  No, bots never become the Judge. Only human players judge the rounds. This keeps
                  the humor and decision-making in human hands where it belongs!
                </p>
              </div>
            </div>
          </section>

          {/* Technical Questions */}
          <section className="faq-category">
            <h2 className="category-title">Technical Questions</h2>

            <div className="faq-item">
              <h3 className="faq-question">What browsers are supported?</h3>
              <div className="faq-answer">
                <p>
                  The game works on all modern browsers including Chrome, Firefox, Safari, Edge, and
                  Opera. Make sure your browser is up to date for the best experience.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">Can I play on mobile?</h3>
              <div className="faq-answer">
                <p>
                  Yes! The game is fully responsive and works on mobile phones and tablets. Just use
                  your mobile browser to visit carnivoresadaptingterribly.com
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">What if I disconnect during a game?</h3>
              <div className="faq-answer">
                <p>
                  We have reconnection handling that will attempt to restore your game state. If you
                  disconnect, try refreshing the page or using the reconnect button. Your cards and
                  progress should be preserved during the same round.
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">Is the game free?</h3>
              <div className="faq-answer">
                <p>
                  Yes, the game is completely free to play! The core game will remain accessible to
                  everyone. We may add optional premium features in the future.
                </p>
              </div>
            </div>
          </section>

          {/* Help & Support */}
          <section className="faq-category">
            <h2 className="category-title">Help & Support</h2>

            <div className="faq-item">
              <h3 className="faq-question">I found a bug. How do I report it?</h3>
              <div className="faq-answer">
                <p>
                  Please use our <Link to="/contact">Contact/Bitch/Whine page</Link> to report bugs.
                  Select "Bug Report" from the dropdown and describe what happened. Include details
                  like:
                </p>
                <ul>
                  <li>What you were doing when the bug occurred</li>
                  <li>What browser you're using</li>
                  <li>Whether you're on mobile or desktop</li>
                  <li>Any error messages you saw</li>
                </ul>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">I have a feature request or suggestion</h3>
              <div className="faq-answer">
                <p>
                  We'd love to hear your ideas! Use the{' '}
                  <Link to="/contact">Contact/Bitch/Whine page</Link> and select "Feature Request"
                  from the dropdown. Tell us what you'd like to see added to the game!
                </p>
              </div>
            </div>

            <div className="faq-item">
              <h3 className="faq-question">How can I contact you?</h3>
              <div className="faq-answer">
                <p>
                  You can reach us through our <Link to="/contact">contact form</Link>.
                </p>
              </div>
            </div>
          </section>
        </div>

        <div className="faq-footer">
          <p>
            <strong>Still have questions?</strong> <Link to="/contact">Contact us</Link> and we'll
            help you out!
          </p>
        </div>
      </div>
    </div>
  );
};

export default FAQ;
